import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/users.js
import { authService } from '@/services/auth';

/**
 * UsersHandler
 * 
 * Manages user data for account management with enterprise features:
 * - Smart caching with configurable freshness thresholds
 * - Request deduplication to prevent duplicate network calls
 * - Background initialization with error recovery
 * - Optimistic UI updates for immediate feedback
 * - Cross-source data retrieval with fallbacks
 * - Permission-aware user operations (create, update, delete)
 * - Main user identification and tracking
 * - User attribute updates for settings management
 */
export class UsersHandler {
  /**
   * Initialize the users handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    this.CACHE_FRESHNESS_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    this.lastUsersFetch = null;
    this.isLoading = false;
    this.pendingRequests = {};

    // Initialize store data structure for users
    this.store.state.data.users = {
      list: [],
      mainUserEmail: null,
      meta: { lastSync: null }
    };

    // Initialize loading state
    this.store.state.loading.users = false;
  }

  /**
   * Initialize the handler and load initial data
   * @returns {Promise<boolean>} Success status
   */
  async initialize() {
    try {
      const projectId = this.store.getProjectId();
      if (projectId) {
        // Try loading users in background during initialization
        setTimeout(async () => {
          try {
            await this.loadUsers(true);
          } catch (error) {
            // Non-critical during init
            if (process.env.NODE_ENV === 'development') {
              console.warn('[UsersHandler] Background user data initialization error:', error);
            }
          }
        }, 200);
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[UsersHandler] Initialization error:', error);
      }
      return false;
    }
  }

  /**
   * Load users for the current project with smart caching
   * @param {boolean} forceRefresh - Force refresh regardless of cache
   * @returns {Promise<Array>} List of user objects
   */
  async loadUsers(forceRefresh = false) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        return [];
      }

      // Return immediately if already have fresh data
      if (!forceRefresh &&
        this.lastUsersFetch &&
        Date.now() - this.lastUsersFetch < this.CACHE_FRESHNESS_THRESHOLD &&
        this.store.state.data.users.list.length > 0) {
        return this.store.state.data.users.list;
      }

      // Check if permissionsService already loaded users (avoid duplicate API call)
      if (!forceRefresh && typeof window !== 'undefined') {
        const { permissionsService } = require('@/services/permissions/permissions');
        if (permissionsService.userDataLoaded && permissionsService.users?.length > 0) {
          // Ensure users data structure exists
          if (!this.store.state.data.users) {
            this.store.state.data.users = { list: [], mainUserEmail: null, meta: { lastSync: null } };
          }
          if (!this.store.state.data.users.meta) {
            this.store.state.data.users.meta = { lastSync: null };
          }
          // Use data from permissionsService
          this.store.state.data.users.list = permissionsService.users;
          this.lastUsersFetch = Date.now();
          this.store.state.data.users.meta.lastSync = new Date().toISOString();
          if (process.env.NODE_ENV === 'development') {
            console.log('[Users] Using data from permissionsService (no API call)');
          }
          return this.store.state.data.users.list;
        }
      }

      // Deduplicate concurrent requests with the same parameters
      const requestKey = `loadUsers-${projectId}`;
      if (this.pendingRequests[requestKey]) {
        return this.pendingRequests[requestKey];
      }

      // Set loading state
      this.isLoading = true;
      this.store.state.loading.users = true;

      // Create and store promise for deduplication
      const loadPromise = (async () => {
        try {
          const authToken = authService.getAuthState()?.token;
          const isPageReload = typeof window !== 'undefined' &&
            window.performance?.navigation?.type === 1;

          // Add timeout to prevent hanging requests
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

          try {
            const response = await fetch(
              `${process.env.GATSBY_STRAPI_URL}/api/user-management/project/${projectId}/users`,
              {
                headers: {
                  'Authorization': `Bearer ${getStrapiToken() || authToken}`
                },
                signal: controller.signal
              }
            );

            clearTimeout(timeoutId);

            if (!response.ok) {
              throw new Error(`Failed to load users: ${response.status}`);
            }

            const data = await response.json();
            const usersList = data.data || [];

            // Find main user email for reference
            const mainUser = usersList.find(user => user.isMainUser);
            const mainUserEmail = mainUser ? mainUser.email.toLowerCase() : null;

            // Update store with user data
            this.store.state.data.users = {
              list: usersList,
              mainUserEmail: mainUserEmail,
              meta: {
                lastSync: new Date().toISOString(),
                count: usersList.length
              }
            };

            this.lastUsersFetch = Date.now();
            return usersList;
          } catch (fetchError) {
            clearTimeout(timeoutId);

            if (isPageReload &&
              (fetchError instanceof TypeError || fetchError.name === 'AbortError') &&
              this.store.state.data.users.list.length > 0) {
              return this.store.state.data.users.list;
            }

            throw fetchError;
          }
        } finally {
          this.isLoading = false;
          this.store.state.loading.users = false;
          delete this.pendingRequests[requestKey];
          this.store.notifySubscribers('users');
        }
      })();

      this.pendingRequests[requestKey] = loadPromise;
      return loadPromise;
    } catch (error) {
      this.store.handleError('users', error);
      this.isLoading = false;
      this.store.state.loading.users = false;
      return [];
    }
  }

  /**
   * Get main user email from the cached user data or profile
   * @returns {string|null} Main user email or null if not found
   */
  getMainUserEmail() {
    // Try getting from users cache first
    if (this.store.state.data.users.mainUserEmail) {
      return this.store.state.data.users.mainUserEmail;
    }

    // Try getting from company profile as fallback
    try {
      const profileData = this.store.state.data.parsedProfile;
      if (profileData?.personal?.Email) {
        return profileData.personal.Email.toLowerCase();
      }
    } catch (error) {
      // Silent fallthrough
    }

    return null;
  }

  /**
   * Check if user exists in the system by email
   * @param {string} email - User email to check
   * @returns {boolean} True if user exists in cached data
   */
  checkUserExists(email) {
    if (!email || !this.store.state.data.users.list.length) {
      return false;
    }

    const normalizedEmail = email.toLowerCase();
    return this.store.state.data.users.list.some(
      user => user.email?.toLowerCase() === normalizedEmail
    );
  }

  /**
   * Create a new user in the system
   * @param {Object} userData - User data to create
   * @param {string} userData.email - User email (required)
   * @param {string} [userData.firstName] - User first name
   * @param {string} [userData.lastName] - User last name
   * @param {string} [userData.role] - User role (defaults to system default)
   * @returns {Promise<Object>} Created user object
   * @throws {Error} If validation fails or user already exists
   */
  async createUser(userData) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      if (!userData.email) {
        throw new Error('Email is required');
      }

      // Check if email already exists in our cache first
      if (this.checkUserExists(userData.email)) {
        throw new Error('A user with this email already exists');
      }

      const authToken = authService.getAuthState()?.token;
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/user-management/project/${projectId}/users`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken() || authToken}`
          },
          body: JSON.stringify(userData)
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || `Failed to create user (${response.status})`);
      }

      const data = await response.json();
      const newUser = data.data;

      if (newUser) {
        // Update local cache with new user
        const updatedList = [...this.store.state.data.users.list, newUser];
        this.store.state.data.users.list = updatedList;
        if (this.store.state.data.users.meta) {
          this.store.state.data.users.meta.count = updatedList.length;
        }
        this.store.notifySubscribers('users');
      }

      // Reload user data to ensure we have latest state
      setTimeout(async () => this.loadUsers(true), 500);

      return newUser;
    } catch (error) {
      if (error.response?.status === 409 || error.message?.includes('already exists')) {
        throw new Error('A user with this email already exists');
      }
      throw error;
    }
  }

  /**
   * Remove a user from the project
   * @param {string} userId - User ID to remove
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If operation fails or parameters are invalid
   */
  async removeUser(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const authToken = authService.getAuthState()?.token;
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/user-management/users/${userId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getStrapiToken() || authToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete user: ${response.status}`);
      }

      // Update local cache by removing the user
      const updatedList = this.store.state.data.users.list.filter(
        user => user.handle !== userId
      );

      this.store.state.data.users.list = updatedList;
      if (this.store.state.data.users.meta) {
        this.store.state.data.users.meta.count = updatedList.length;
      }
      this.store.notifySubscribers('users');

      // Reload user data to ensure we have latest state
      setTimeout(async () => this.loadUsers(true), 500);

      return true;
    } catch (error) {
      this.store.handleError('users', error);
      throw error;
    }
  }

  /**
   * Update a user's role
   * @param {string} userId - User ID to update
   * @param {string} role - New role for the user (e.g. 'Admin', 'Member')
   * @returns {Promise<Object>} Updated user data
   * @throws {Error} If operation fails or parameters are invalid
   */
  async updateUserRole(userId, role) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      if (!role) {
        throw new Error('Role is required');
      }

      const authToken = authService.getAuthState()?.token;
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/user-management/users/${userId}/role`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken() || authToken}`
          },
          body: JSON.stringify({ role })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update user role: ${response.status}`);
      }

      // Update user in local cache
      const updatedList = this.store.state.data.users.list.map(user => {
        if (user.handle === userId) {
          return { ...user, role };
        }
        return user;
      });

      this.store.state.data.users.list = updatedList;
      this.store.notifySubscribers('users');

      // Reload user data to ensure we have latest state
      setTimeout(async () => this.loadUsers(true), 500);

      return { userId, role, success: true };
    } catch (error) {
      this.store.handleError('users', error);
      throw error;
    }
  }

  /**
   * Update a specific user attribute (for settings page)
   * Used for updating individual profile fields like firstName, lastName, jobTitle, phoneNumber
   * 
   * @param {string} email - User's email address
   * @param {string} attributeName - Name of the attribute to update
   * @param {string} attributeValue - New value for the attribute
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If operation fails or parameters are invalid
   */
  async updateUserAttribute(email, attributeName, attributeValue) {
    try {
      if (!email) {
        throw new Error('Email is required');
      }

      if (!attributeName) {
        throw new Error('Attribute name is required');
      }

      const authToken = authService.getAuthState()?.token;
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/user-management/update-attribute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken() || authToken}`
          },
          body: JSON.stringify({
            email,
            attributeName,
            attributeValue: attributeValue || ''
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update ${attributeName}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || `Failed to update ${attributeName}`);
      }

      // Update local cache to reflect the change
      const currentUserEmail = authService.getAuthState()?.user?.email?.toLowerCase();
      if (currentUserEmail === email.toLowerCase()) {
        // Find and update the current user in the cache
        const updatedList = this.store.state.data.users.list.map(user => {
          if (user.email?.toLowerCase() === email.toLowerCase()) {
            // Map attribute names to user object properties
            const attributeMap = {
              'firstName': 'name',
              'lastName': 'name',
              'jobTitle': 'jobTitle',
              'jobtitle': 'jobTitle',
              'phoneNumber': 'phoneNumber'
            };

            const updatedUser = { ...user };

            // Handle name updates specially (firstName + lastName)
            // user.name is a combined field like "Alex Hitchcock" — split carefully
            // since last names can be multi-word (e.g. "ONE user")
            if (attributeName === 'firstName' || attributeName === 'lastName') {
              if (attributeName === 'firstName') {
                const lastName = user.name ? user.name.split(' ').slice(1).join(' ') : '';
                updatedUser.name = [attributeValue, lastName].filter(Boolean).join(' ').trim();
              } else {
                const firstName = user.name ? user.name.split(' ')[0] : '';
                updatedUser.name = [firstName, attributeValue].filter(Boolean).join(' ').trim();
              }
            } else {
              // Handle other attributes
              const userProperty = attributeMap[attributeName];
              if (userProperty) {
                updatedUser[userProperty] = attributeValue;
              }
            }

            return updatedUser;
          }
          return user;
        });

        this.store.state.data.users.list = updatedList;
        this.store.notifySubscribers('users');

        // Persist optimistic update to sessionStorage so it survives page reload.
        // Without this, restoreState() on reload would show the stale cached name.
        if (this.store.saveState) {
          this.store.saveState();
        }
      }

      // NOTE: No background loadUsers(true) here. The optimistic cache update above
      // is persisted to sessionStorage and is the source of truth until next full load.
      // A premature refresh can revert the optimistic update because Keycloak's admin
      // API may return stale data from its internal cache (Infinispan) for a few seconds
      // after a write. Fresh data will load naturally on next navigation/page load.

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`[UsersHandler] Error updating attribute ${attributeName}:`, error);
      }
      throw error;
    }
  }

  /**
   * Check if users data is stale and needs refreshing
   * @returns {boolean} True if data is stale or doesn't exist
   */
  isUserDataStale() {
    return !this.lastUsersFetch ||
      (Date.now() - this.lastUsersFetch > this.CACHE_FRESHNESS_THRESHOLD) ||
      this.store.state.data.users.list.length === 0;
  }

  /**
   * Get current user from cache
   * @returns {Object|null} Current user object or null if not found
   */
  getCurrentUser() {
    const authState = authService.getAuthState();
    const currentEmail = authState?.user?.email?.toLowerCase();

    if (!currentEmail || !this.store.state.data.users.list.length) {
      return null;
    }

    return this.store.state.data.users.list.find(user =>
      user.email?.toLowerCase() === currentEmail
    ) || null;
  }
}